'use client';
import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import InfiniteScroll from '@/components/InfiniteScroll';
import Pagination from '@/components/Pagination';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { IconButton, Paper, Tooltip } from '@mui/material';
import { QuestionFilled, QuestionOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { Fragment, useEffect, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { useInviteFriendStore } from '../store/invite-friend-store';

const statusColorMapping = {
  Unregistered: 'text-error',
  Unverified: 'text-error',
  Purchased: 'text-success',
  Verified: 'text-success',
};

interface TrackInvitationProps {
  // 这些props用于初始值
  list: any[];
  pagination: { total: number; limit: number; page: number };
  deviceType: EPlatform;
  inviteData: GetResponseType<ApiResponseDefinition['GET:/api/customer/invite_register']>;
  isAuthenticated: boolean;
}

const TrackInvatationStatus = (props: TrackInvitationProps) => {
  const { deviceType, list, pagination, inviteData } = props;
  const token = useStore((state) => state.token);
  const [isAuthenticated, setIsAuthenticated] = useState(props.isAuthenticated);
  const t = useTranslations('account.referAFriend.trackInvitation_2');

  // 从store中获取最新的数据
  // TODO: 使用useShallow优化：zustand The result of getServerSnapshot should be cached to avoid an infinite loop
  const {
    openRuleDialogWithType,
    invitationList,
    pagination: storePagination,
    refreshInvitationHistory,
    updateInvitationList,
    updatePagination,
    isLoading,
  } = useInviteFriendStore(
    useShallow((state) => ({
      openRuleDialogWithType: state.openRuleDialogWithType,
      invitationList: state.invitationList,
      pagination: state.pagination,
      refreshInvitationHistory: state.refreshInvitationHistory,
      updateInvitationList: state.updateInvitationList,
      updatePagination: state.updatePagination,
      isLoading: state.isLoading,
    })),
  );

  // 页面加载时，如果store中没有数据但props有初始数据，则初始化store
  useEffect(() => {
    if (list.length > 0) {
      updateInvitationList(list);
      updatePagination(pagination);
    }
  }, [list, pagination]);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
    }
  }, [token]);

  // 当分页变化时，通过store更新数据，Web用的
  const handlePageChange = async (value: number, isConcat = false) => {
    // 通过store更新分页页码
    updatePagination({ page: value, total: storePagination.total, limit: storePagination.limit });

    // 通过store的refreshInvitationHistory方法获取新页的数据
    await refreshInvitationHistory(isConcat);
  };

  const renderMobileItem = (item: any) => {
    return (
      <div
        key={item.email}
        className="w-full mb-[2.133vw] p-[4.8vw_3.733vw_4vw] box-border bg-[#f5f6f7] rounded-[2.4vw]"
      >
        <div className="flex flex-nowrap flex-row justify-between items-center mb-[2.667vw]">
          <span className="text-[3.733vw] text-[#000000]">
            {item.invitee_email?.length > 22
              ? `${item.invitee_email.slice(0, 22)}...`
              : item.invitee_email}
          </span>
          <span className={`text-[3.2vw] ${statusColorMapping[item.show_status] || ''}`}>
            {item.show_status}
          </span>
        </div>
        <div className="flex flex-nowrap flex-row justify-between items-center">
          <span className="flex items-center text-[3.2vw] text-[gray]">{item.show_date}</span>
          <p className="flex items-center">
            <span className="text-[3.733vw] text-[gray]">
              {item.show_points && item.show_points !== 0 ? item.show_points : t('noData')}
            </span>
          </p>
        </div>
      </div>
    );
  };

  return invitationList.length && isAuthenticated ? (
    <div className="mt-[2.667vw] lg:mt-0 lg:w-[60.83vw] mx-auto bg-white lg:bg-transparent rounded-[3.2vw] lg:rounded-none  w-full  overflow-hidden">
      <p className="bg-[#b2bfff] lg:bg-transparent h-[12.533vw] lg:h-auto leading-[12.533vw] lg:leading-normal text-left pl-[4vw]  f16 mb-7 font-bb f16 lg:text-center lg:mt-[2.6vw] lg:mb-[1.146vw]  lg:f16  lg:flex lg:items-center lg:justify-center">
        {t('title')}
        <WapOnly>
          <IconButton
            size="small"
            className="ml-1"
            onClick={() => {
              openRuleDialogWithType('status');
            }}
          >
            <QuestionOutlined className="text-primary-text text-[3.733vw]" />
          </IconButton>
        </WapOnly>
        <WebOnly>
          <Tooltip
            arrow
            placement="top"
            title={
              <>
                <p>{t('statusTooltip.unregistered')}</p>
                <p>{t('statusTooltip.unverified')}</p>
                <p>{t('statusTooltip.verified')}</p>
                <p>{t('statusTooltip.purchased')}</p>
              </>
            }
          >
            <IconButton size="small" className="ml-1" onClick={() => {}}>
              <QuestionFilled className="text-[#999999] text-[12px]" />
            </IconButton>
          </Tooltip>
        </WebOnly>
      </p>

      <WapOnly>
        <div className="flex flex-col bg-white p-[3.2vw] rounded-[0_0_3.2vw_3.2vw]">
          {invitationList.map((it) => (
            <Fragment key={it.email || it.id}>{renderMobileItem(it)}</Fragment>
          ))}
        </div>
        <InfiniteScroll
          loading={isLoading}
          page={storePagination.page}
          total={storePagination.total}
          limit={storePagination.limit}
          onReachBottom={() => handlePageChange(storePagination.page + 1, true)}
        />
      </WapOnly>
      <WebOnly>
        <Paper elevation={0} className="border border-[#dce0e8] rounded-lg overflow-hidden mb-10">
          {/* Table Header */}
          <div className="flex bg-[#f6f7f9] font-bb f14 py-4 px-[70px] border-b border-[#dce0e8]">
            <div className="w-[32%] pr-[15px]">{t('tableHeaders.emails')}</div>
            <div className="w-[29%] pr-[15px]">{t('tableHeaders.date')}</div>
            <div className="w-[22%] pr-[15px]">{t('tableHeaders.status')}</div>
            <div className="flex-grow">{t('tableHeaders.points')}</div>
          </div>

          {/* Table Body */}
          {invitationList.map((item) => (
            <div
              key={item.id}
              className="flex items-center text-sm py-4 px-[70px] border-b border-[#dce0e8] last:border-b-0"
            >
              <Tooltip
                title={item.invitee_email}
                placement="top-start"
                arrow
                disableHoverListener={!item.invitee_email || item.invitee_email.length < 31}
              >
                <div className="w-[32%] pr-[15px] overflow-hidden text-ellipsis">
                  {item.invitee_email?.length > 22
                    ? `${item.invitee_email.slice(0, 22)}...`
                    : item.invitee_email}
                </div>
              </Tooltip>

              <div className="w-[29%] pr-[15px]">{item.show_date}</div>

              <div className={`w-[22%] pr-[15px] ${statusColorMapping[item.show_status] || ''}`}>
                {item.show_status}
              </div>

              <div className="flex-grow flex items-center">
                {item.show_points && +item.show_points !== 0 ? (
                  <span>+{item.show_points}</span>
                ) : (
                  <span>{t('noData')}</span>
                )}
                {}
                {(item.show_status === 'Purchased' || item.show_status === 'Verified') &&
                +item.show_points === 0 ? (
                  deviceType !== EPlatform.WEB ? (
                    <IconButton
                      size="small"
                      className="ml-2"
                      onClick={() => {
                        openRuleDialogWithType(item.show_status);
                      }}
                    >
                      <QuestionOutlined className="text-[3.467vw] text-secondary-text" />
                    </IconButton>
                  ) : (
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        item.show_status === 'Purchased'
                          ? t('pointsLimits.registrationLimit', {
                              dayLimit: inviteData.invite_rule.refer_friend.day_limit,
                            })
                          : t('pointsLimits.purchaseLimit', {
                              dayLimit: inviteData.invite_rule.refer_order.day_limit,
                            })
                      }
                    >
                      <IconButton size="small" className="ml-2" onClick={() => {}}>
                        <QuestionFilled fontSize="small" className="text-[#999999]" />
                      </IconButton>
                    </Tooltip>
                  )
                ) : null}
              </div>
            </div>
          ))}
        </Paper>

        {/* Pagination */}
        {storePagination.total > storePagination.limit && (
          <div className="flex justify-center pb-[60px]">
            <Pagination
              total={storePagination.total}
              current={storePagination.page}
              pageSize={storePagination.limit}
              onChange={(_, value) => handlePageChange(value)}
            />
          </div>
        )}
      </WebOnly>
    </div>
  ) : null;
};

export default TrackInvatationStatus;
